import { Temporal } from '@js-temporal/polyfill'

/**
 * Timezone utility functions that work on both client and server
 */

/**
 * Parses a datetime string into a Temporal.PlainDateTime object
 * Accepts formats: "YYYY-MM-DDTHH:MM" or "YYYY-MM-DDTHH:MM:SS"
 *
 * Use this for input validation and date calculations (add/subtract days, hours, etc.)
 */
export function parsePlainDateTime(dateTimeString: string): Temporal.PlainDateTime {
	// Ensure the datetime string includes seconds (datetime-local input format doesn't include them)
	const dateTimeWithSeconds =
		dateTimeString.includes(`:`) && dateTimeString.split(`:`).length === 2
			? `${dateTimeString}:00`
			: dateTimeString

	// Parse the datetime string components
	// Format: YYYY-MM-DDTHH:MM:SS
	const match = dateTimeWithSeconds.match(
		/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/,
	)
	if (!match) {
		throw new Error(
			`Invalid datetime format: ${dateTimeString}. Expected YYYY-MM-DDTHH:MM or YYYY-MM-DDTHH:MM:SS`,
		)
	}

	const [, year, month, day, hour, minute, second] = match

	return Temporal.PlainDateTime.from({
		year: parseInt(year!, 10),
		month: parseInt(month!, 10),
		day: parseInt(day!, 10),
		hour: parseInt(hour!, 10),
		minute: parseInt(minute!, 10),
		second: parseInt(second!, 10),
	})
}

/**
 * Converts a Temporal.PlainDateTime to a UTC Date object for database storage
 * Interprets the PlainDateTime as being in the specified timezone
 */
export function plainDateTimeToUTC(
	plainDateTime: Temporal.PlainDateTime,
	timezone: string,
): Date {
	// Convert to ZonedDateTime in the target timezone
	const zonedDateTime = plainDateTime.toZonedDateTime(timezone)

	// Convert to UTC instant and then to JavaScript Date
	const instant = zonedDateTime.toInstant()
	return new Date(instant.epochMilliseconds)
}

/**
 * Converts a UTC Date object to a Temporal.PlainDateTime in the specified timezone
 * Use this for date calculations after retrieving from database
 */
export function utcToPlainDateTime(
	utcDate: Date,
	timezone: string,
): Temporal.PlainDateTime {
	// Convert Date to Temporal.Instant
	const instant = Temporal.Instant.fromEpochMilliseconds(utcDate.getTime())

	// Convert to ZonedDateTime in the target timezone
	const zonedDateTime = instant.toZonedDateTimeISO(timezone)

	// Return as PlainDateTime for calculations
	return zonedDateTime.toPlainDateTime()
}

/**
 * @deprecated Use parsePlainDateTime and plainDateTimeToUTC instead
 * Converts a local datetime string to UTC Date object
 */
export function convertLocalToUTC(
	localDateTime: string,
	timezone: string,
): Date {
	const plainDateTime = parsePlainDateTime(localDateTime)
	return plainDateTimeToUTC(plainDateTime, timezone)
}

/**
 * Formats a UTC Date object in a specific timezone
 */
export function formatInTimezone(
	date: Date,
	timezone: string,
	options: Intl.DateTimeFormatOptions = {},
): string {
	const defaultOptions: Intl.DateTimeFormatOptions = {
		year: `numeric`,
		month: `long`,
		day: `numeric`,
		hour: `2-digit`,
		minute: `2-digit`,
		timeZone: timezone,
	}

	return new Intl.DateTimeFormat(`en-US`, {
		...defaultOptions,
		...options,
	}).format(date)
}

/**
 * Gets the timezone abbreviation for a given timezone at a specific date
 * Returns the short form like "CET", "EST", etc.
 */
export function getTimezoneAbbreviation(
	timezone: string,
	date: Date = new Date(),
): string {
	const formatter = new Intl.DateTimeFormat(`en-US`, {
		timeZone: timezone,
		timeZoneName: `short`,
	})

	const parts = formatter.formatToParts(date)
	const timeZonePart = parts.find((part) => part.type === `timeZoneName`)

	return timeZonePart?.value || timezone
}

/**
 * Formats an event date in user-friendly format: "Friday, November 21"
 * Use this for consistent date display across the application
 */
export function formatEventDate(
	utcDate: Date,
	timezone: string,
): string {
	const dateParts = new Intl.DateTimeFormat(`en-US`, {
		weekday: `long`,
		month: `long`,
		day: `numeric`,
		timeZone: timezone,
	}).formatToParts(utcDate)

	const weekday = dateParts.find((p) => p.type === `weekday`)?.value
	const month = dateParts.find((p) => p.type === `month`)?.value
	const day = dateParts.find((p) => p.type === `day`)?.value

	return `${weekday}, ${month} ${day}`
}

/**
 * Formats a date range for display in a specific timezone
 * Returns date in format "Friday, November 21" and time range
 */
export function formatDateRangeInTimezone(
	startDate: Date,
	endDate: Date,
	timezone: string,
): {
	date: string
	time: string
	full: string
	timezone: string
	timezoneAbbr: string
} {
	// Format date in user-friendly format: "Friday, November 21"
	const date = formatEventDate(startDate, timezone)

	// Format times in 24-hour format
	const startTime = new Intl.DateTimeFormat(`en-US`, {
		hour: `2-digit`,
		minute: `2-digit`,
		hour12: false,
		timeZone: timezone,
	}).format(startDate)

	const endTime = new Intl.DateTimeFormat(`en-US`, {
		hour: `2-digit`,
		minute: `2-digit`,
		hour12: false,
		timeZone: timezone,
	}).format(endDate)

	const timezoneAbbr = getTimezoneAbbreviation(timezone, startDate)

	return {
		date,
		time: `${startTime} - ${endTime}`,
		full: `${date} from ${startTime} to ${endTime} ${timezoneAbbr}`,
		timezone,
		timezoneAbbr,
	}
}

/**
 * Checks if a given string is a valid IANA timezone identifier
 */
export function isValidTimezone(timezone: string): boolean {
	try {
		new Intl.DateTimeFormat(`en-US`, { timeZone: timezone })
		return true
	} catch {
		return false
	}
}

/**
 * Converts a datetime-local input value (YYYY-MM-DDTHH:MM) to UTC
 * assuming it's in the specified timezone
 */
export function parseLocalDateTimeToUTC(
	localDateTimeString: string,
	timezone: string,
): Date {
	if (!localDateTimeString) {
		throw new Error(`Local datetime string is required`)
	}

	if (!isValidTimezone(timezone)) {
		throw new Error(`Invalid timezone: ${timezone}`)
	}

	return convertLocalToUTC(localDateTimeString, timezone)
}

/**
 * Formats a UTC date to a datetime-local input format (YYYY-MM-DDTHH:MM)
 * in the specified timezone
 */
export function formatUTCToLocalDateTime(
	utcDate: Date,
	timezone: string,
): string {
	const formatter = new Intl.DateTimeFormat(`sv-SE`, {
		year: `numeric`,
		month: `2-digit`,
		day: `2-digit`,
		hour: `2-digit`,
		minute: `2-digit`,
		timeZone: timezone,
	})

	return formatter.format(utcDate).replace(` `, `T`)
}
